import type { MetricAxisSpec, MetricXTicks } from '@/components/charts/generic/axisTypes';
import { CHARTJS_MAX_TICKS_LIMIT } from '@/components/charts/generic/constants';
import type { MetricPointMarker, MetricSeriesSpec } from '@/components/charts/generic/seriesTypes';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { formatShortMonthDay } from '@/components/charts/generic/xAxisScale';
import { graphSettings } from '@/config/settings';
import type { DailyWeightTimeSeries, WeightDistributionBucket } from '@/domain/smoltReport';
import { formatDateShortLocalized } from '@/utils/date';
import { formatPercentageByLocale, type Locale } from '@/utils/localeFormatters';

/**
 * MetricChart specs for the Smolt report. The report is read and printed as a whole, so none of
 * its charts zoom, and every line tooltip is titled with the short date. The date axis always
 * labels the first and last day of the report, and the lines curve only gently.
 */

const WEIGHT_DISTRIBUTION_BAR_COLOR = graphSettings.graphColors.weightDistribution;
export const LAST_FEED_LINE_COLOR = graphSettings.graphColors.dayMarker;

/** A measured point drawn as a ring around a hollow centre. */
const HOLLOW_MARKER: MetricPointMarker = { radius: 4, hoverRadius: 4, fill: 'surface' };
/** A scatter point: the ring alone, with nothing filled in. */
const RING_MARKER: MetricPointMarker = { radius: 4, hoverRadius: 4, fill: 'none' };
/** A fitted line, drawn without markers. */
const NO_MARKER: MetricPointMarker = { radius: 0 };

/** Gentler than the shared smoothing, so a sparse series does not overshoot between days. */
const LINE_TENSION = 0.2;
/** Stands in for the leading space the swatch and value were once parted by. */
const SWATCH_GAP = 4;
/** Date labels on the weight and growth charts. */
const TIME_SERIES_X_TICKS = 10;
/** Date labels on the narrower welfare chart. */
const WELFARE_X_TICKS = 7;

export type SmoltSeriesStyle = 'line' | 'points' | 'trend';

const STYLE: Record<SmoltSeriesStyle, Pick<MetricSeriesSpec, 'marker' | 'showLine'>> = {
    line: { marker: HOLLOW_MARKER },
    points: { marker: RING_MARKER, showLine: false },
    trend: { marker: NO_MARKER },
};

function sortedDays(days: string[]): string[] {
    return [...new Set(days)].sort((a, b) => a.localeCompare(b));
}

function dateTicks(locale: Locale, maxTicksLimit: number): MetricXTicks {
    return { maxTicksLimit, keepEnds: true, format: (day) => formatShortMonthDay(day, locale) };
}

function lineChartBase(
    locale: Locale,
    maxXTicks: number
): Pick<
    MetricChartSpec<'line'>,
    'kind' | 'locale' | 'zoom' | 'tooltipTitle' | 'tooltipBoxPadding' | 'xTicks'
> {
    return {
        kind: 'line',
        locale,
        zoom: false,
        tooltipTitle: (day) => formatDateShortLocalized(day, locale),
        tooltipBoxPadding: SWATCH_GAP,
        xTicks: dateTicks(locale, maxXTicks),
    };
}

/** Axis of a Smolt chart: `unit` with its tick and tooltip decimals. */
export function smoltAxis(
    unit: string,
    tickDecimals: number,
    valueDecimals: number
): MetricAxisSpec {
    return { label: '', unit, tickDecimals, valueDecimals, maxTicksLimit: CHARTJS_MAX_TICKS_LIMIT };
}

/** As the cage's Weight graphs show live weight: whole grams on the axis, 2 decimals in the tooltip. */
export const LIVING_WEIGHT_AXIS: MetricAxisSpec = smoltAxis('g', 0, 2);

export interface WelfareDataPoint {
    day: string;
    value: number | null;
}

export interface WelfareDataset {
    label: string;
    color: string;
    data: WelfareDataPoint[];
}

/** The welfare development lines: one per dataset, in percent, with hollow markers. */
export function buildWelfareChartSpec(
    datasets: WelfareDataset[],
    locale: Locale,
    decimals: number
): MetricChartSpec<'line'> {
    return {
        ...lineChartBase(locale, WELFARE_X_TICKS),
        // A score without a value that day has nothing to say in the tooltip.
        tooltipSkipEmpty: true,
        days: sortedDays(datasets.flatMap((dataset) => dataset.data.map((point) => point.day))),
        series: datasets.map((dataset, index) => ({
            key: `welfare:${index}`,
            label: dataset.label,
            color: dataset.color,
            showInLegend: false,
            ...STYLE.line,
            tension: LINE_TENSION,
            data: dataset.data,
        })),
        left: smoltAxis('%', decimals, decimals),
    };
}

export interface SmoltTimeSeriesLine {
    key: string;
    label: string;
    color: string;
    style: SmoltSeriesStyle;
    value: (day: DailyWeightTimeSeries) => number | null;
    axis?: 'left' | 'right';
}

export interface SmoltTimeSeriesChartParams {
    timeSeries: DailyWeightTimeSeries[];
    lines: SmoltTimeSeriesLine[];
    left: MetricAxisSpec;
    right?: MetricAxisSpec;
    locale: Locale;
    /** Marked with a dashed vertical line when it falls on a day of the series. */
    lastFeedingDate?: string | null;
}

/** One of the weight and growth time series charts. */
export function buildSmoltTimeSeriesSpec(
    params: SmoltTimeSeriesChartParams
): MetricChartSpec<'line'> {
    const { timeSeries, lines, left, right, locale, lastFeedingDate } = params;
    return {
        ...lineChartBase(locale, TIME_SERIES_X_TICKS),
        days: sortedDays(timeSeries.map((day) => day.day)),
        series: lines.map((line) => ({
            key: line.key,
            label: line.label,
            color: line.color,
            axis: line.axis,
            showInLegend: false,
            ...STYLE[line.style],
            tension: LINE_TENSION,
            data: timeSeries.map((day) => ({ day: day.day, value: line.value(day) })),
        })),
        left,
        right,
        verticalLines: lastFeedingDate
            ? [{ key: 'lastFeed', at: lastFeedingDate, color: LAST_FEED_LINE_COLOR }]
            : [],
    };
}

export interface SmoltMeasurementsChartParams {
    timeSeries: DailyWeightTimeSeries[];
    /** Already translated. */
    label: string;
    color: string;
    locale: Locale;
    /** Marked with a dashed vertical line when it falls on a day of the series. */
    lastFeedingDate?: string | null;
}

/** Fish measured per day, one bar per day, from stocking to delivery. */
export function buildSmoltMeasurementsSpec(
    params: SmoltMeasurementsChartParams
): MetricChartSpec<'bar'> {
    const { timeSeries, label, color, locale, lastFeedingDate } = params;
    return {
        kind: 'bar',
        locale,
        zoom: false,
        tooltipTitle: (day) => formatDateShortLocalized(day, locale),
        days: sortedDays(timeSeries.map((day) => day.day)),
        series: [
            {
                key: 'numMeasurements',
                label,
                color,
                showInLegend: false,
                data: timeSeries.map((day) => ({
                    day: day.day,
                    value: day.numMeasurements,
                })),
            },
        ],
        xTicks: dateTicks(locale, TIME_SERIES_X_TICKS),
        left: {
            label: '',
            beginAtZero: true,
            tickDecimals: 0,
            valueDecimals: 0,
            maxTicksLimit: CHARTJS_MAX_TICKS_LIMIT,
        },
        verticalLines: lastFeedingDate
            ? [{ key: 'lastFeed', at: lastFeedingDate, color: LAST_FEED_LINE_COLOR }]
            : [],
    };
}

/** Whether the last feeding day falls on the series, which is when its line is drawn. */
export function hasLastFeedLine(
    timeSeries: DailyWeightTimeSeries[],
    lastFeedingDate: string | null | undefined
): boolean {
    return !!lastFeedingDate && timeSeries.some((day) => day.day === lastFeedingDate);
}

function bucketLabel(bucket: WeightDistributionBucket): string {
    return `${bucket.from ?? 0} - ${bucket.to ?? 0} g`;
}

/** Highest share plus a little headroom, so the tallest bar does not touch the top. */
export function calculateWeightDistributionMaxY(distribution: WeightDistributionBucket[]): number {
    if (!distribution?.length) return 0;
    return Math.max(...distribution.map((item) => item.percentage ?? 0)) + 0.5;
}

/** The delivery weight distribution: one bar per weight bucket, nearly touching. */
export function buildWeightDistributionSpec(
    distribution: WeightDistributionBucket[],
    shareLabel: string,
    locale: Locale
): MetricChartSpec<'bar'> {
    const sorted = [...(distribution ?? [])].sort(
        (a, b) => (a.from ?? 0) - (b.from ?? 0) || (a.to ?? 0) - (b.to ?? 0)
    );
    return {
        kind: 'bar',
        days: sorted.map(bucketLabel),
        xAxisKind: 'category',
        series: [
            {
                key: 'share',
                label: shareLabel,
                color: WEIGHT_DISTRIBUTION_BAR_COLOR,
                borderRadius: 2,
                barPercentage: 0.98,
                categoryPercentage: 0.98,
                showInLegend: false,
                data: sorted.map((item) => ({
                    day: bucketLabel(item),
                    value: item.percentage ?? 0,
                })),
            },
        ],
        // Bucket labels packed as closely as they fit, as the histogram always had them.
        xTicks: { autoSkipPadding: 0, padding: 0 },
        left: {
            label: '',
            beginAtZero: true,
            max: calculateWeightDistributionMaxY(distribution),
            maxTicksLimit: CHARTJS_MAX_TICKS_LIMIT,
            format: (value) => formatPercentageByLocale(value, locale, 1),
        },
        locale,
        zoom: false,
    };
}
